/**
 * What the docked result strip says (Option C, C2, rule 3b.7): ONE shape for every engine and every test path (Test,
 * Save's test, a fix retry) — the notice (the answer), the legs as chips where the backend reports legs (the SSH tunnel
 * and the database; one per Azure Storage service), and Save anyway / Update anyway on every failure, worded for the
 * kind of error. Pure, so the copy and the per-kind choices are unit-tested without the dialog.
 *
 * SSH runs used to show a numbered stepper with no notice, no fix chips and no Save anyway; they now get all three.
 */
import type { Connection, TestStepwiseResult } from '@/types/electron';
import { classifyDbError } from '@/core/analytics/dbErrorClassifier';
import type { AnalyticsErrorCategory } from '@/core/analytics/analyticsTypes';
import type { ConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import type { TestLeg } from '../../components/TestConnectionStepper';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { fieldApplies } from '../state/connectionFieldRules';
import { sshStripFailure } from './sshStripFailure';
import { storageFieldSelector, storageSaveAnywayRefusal } from './storageStripFailure';
import { connectionTargetLine } from '../../utils/connectionServerLine';
import { redactTargetSecrets } from '@/types/connectionTransfer';

export type StripTone = 'progress' | 'success' | 'warning' | 'failure';

export type StripNotice =
  | { kind: 'success'; text: string }
  | { kind: 'warning'; text: string }
  /** A failed connect: the error notice (title, plain message, fix chip, Error details). `ssh`: the tunnel failed. */
  | { kind: 'error'; error: string; connection?: Connection; errorCode?: string; origin?: 'ssh'; title?: string };

export type ResultStripModel = {
  tone: StripTone;
  notice?: StripNotice;
  /**
   * Azure Storage runs: the one line over the rows ("Blob verified · Table refused"; round B N1 — the essentials even in
   * a short region). When nothing verified it also says what that means (the notice of a storage run).
   */
  legsSummary?: { text: string; failed: boolean };
  legs?: TestLeg[];
  /** Save anyway's line, worded for the error kind; absent when Save anyway does not apply. */
  saveAnyway?: string;
  /**
   * A failure Save anyway is NOT offered for (C2 review C2-01 / C2-02): a validation or security refusal of the settings
   * themselves. The reason replaces the Save anyway line, and `useConnectionSave` refuses the action too.
   */
  saveAnywayWithheld?: string;
  /** The field the failure is about: scrolled into view (never focused) when the answer arrives. */
  fieldSelector?: string;
  /** The short tier's one line. */
  summary: string;
};

type SaveAnywayKind = 'network' | 'auth' | 'tls' | 'database' | 'file' | 'ssh-network' | 'ssh-auth' | 'storage' | 'other';

const NETWORK: ReadonlySet<AnalyticsErrorCategory> = new Set(['timeout', 'network', 'dns_not_found', 'conn_refused', 'host_unreachable', 'firewall_blocked', 'instance_not_found']);
const AUTH: ReadonlySet<AnalyticsErrorCategory> = new Set(['auth_failed', 'permission']);
const TLS: ReadonlySet<AnalyticsErrorCategory> = new Set(['ssl', 'tls_cert_untrusted', 'tls_required', 'tls_unsupported']);

/** "Save it anyway …" per kind (edit mode: "Update it anyway …"). */
export function saveAnywayPrompt(kind: SaveAnywayKind, isEditMode: boolean): string {
  const it = `${isEditMode ? 'Update' : 'Save'} it anyway`;
  switch (kind) {
    case 'network': return `Server unreachable right now? ${it} and connect later.`;
    case 'auth': return `${it} and fix the login later.`;
    case 'tls': return `${it} and fix the encryption settings later.`;
    case 'database': return `${it} and fix the database name later.`;
    case 'file': return `${it} and fix the file path later.`;
    case 'ssh-network': return `SSH host unreachable right now? ${it} and connect later.`;
    case 'ssh-auth': return `${it} and fix the SSH sign-in later.`;
    case 'storage': return `${it} and test the services later.`;
    default: return `${it} and fix the settings later.`;
  }
}

function saveAnywayKind(category: AnalyticsErrorCategory): SaveAnywayKind {
  if (NETWORK.has(category)) return 'network';
  if (AUTH.has(category)) return 'auth';
  if (TLS.has(category)) return 'tls';
  if (category === 'db_not_found') return 'database';
  if (category === 'file_not_found') return 'file';
  return 'other';
}

/** Where the connection dials: the TNS field in Oracle's TNS modes, else Server / Host / the file / the cluster. */
function targetSelector(connection: Connection | undefined): string {
  if (connection?.engine === 'oracle' && connection.oracleConnectionStringType === 'tns_alias') return '#oracleTnsAlias';
  if (connection?.engine === 'oracle' && connection.oracleConnectionStringType === 'tns_descriptor') return '#oracleTnsDescriptor';
  return '#server';
}

/** The field a classified connect failure is about. */
export function errorFieldSelector(category: AnalyticsErrorCategory, connection: Connection | undefined): string | undefined {
  if (NETWORK.has(category) || category === 'file_not_found') return targetSelector(connection);
  if (category === 'db_not_found') return '#database';
  if (TLS.has(category)) return connection?.engine === 'mssql' ? '#encrypt' : '#ssl';
  if (AUTH.has(category) && connection) {
    const values = { engine: connection.engine as DatabaseEngine | undefined, authentication: connection.authentication };
    if (fieldApplies('login', values)) return '#login';
    return fieldApplies('password', values) ? '#password' : '#authentication';
  }
  return undefined;
}

/**
 * "db.example:5432": where the run dialed (`connectionTargetLine` — no automatic or zero port, the Oracle descriptor as
 * host / port / service, secrets masked; C2 review C2-03).
 */
export function dialedTarget(connection: Parameters<typeof connectionTargetLine>[0] | undefined): string {
  return connection ? connectionTargetLine(connection) : '';
}

function stepwiseModel(outcome: ConnectionTestOutcome, stepwise: TestStepwiseResult, isEditMode: boolean): ResultStripModel {
  const connection = outcome.connection;
  const bastion = connection?.sshTunnel?.host;
  const target = dialedTarget(connection);
  const sshFailed = stepwise.ssh?.status === 'error';
  const dbFailed = stepwise.db.status === 'error';
  const legs: TestLeg[] = [];
  if (stepwise.ssh && stepwise.ssh.status !== 'skipped') {
    const chip = sshFailed ? sshStripFailure(stepwise.ssh.error).chip : bastion ? `Open through ${bastion}` : 'Open';
    legs.push({ id: 'ssh', label: 'SSH tunnel', status: sshFailed ? 'error' : 'ok', detail: chip });
  }
  const dbStatus = stepwise.db.status === 'not-attempted' ? 'not-attempted' : stepwise.db.status;
  legs.push({ id: 'db', label: 'Database', status: dbStatus, detail: dbStatus === 'ok' ? (target ? `Connected to ${target}` : 'Connected') : dbStatus === 'error' ? 'Failed' : 'Not attempted' });

  if (sshFailed) {
    const failure = sshStripFailure(stepwise.ssh?.error);
    return {
      tone: 'failure', legs, fieldSelector: failure.field,
      ...('withheld' in failure.saveAnyway ? { saveAnywayWithheld: failure.saveAnyway.withheld } : { saveAnyway: saveAnywayPrompt(failure.saveAnyway.kind, isEditMode) }),
      notice: { kind: 'error', error: stepwise.ssh?.detail ?? 'The SSH tunnel could not be opened.', connection, origin: 'ssh', title: failure.title },
      summary: `${failure.title}: ${stepwise.ssh?.detail ?? ''}`.trim(),
    };
  }
  if (dbFailed) {
    const error = stepwise.db.error ?? stepwise.db.detail;
    const { category } = classifyDbError(connection?.engine, error);
    return {
      tone: 'failure', legs, fieldSelector: errorFieldSelector(category, connection), saveAnyway: saveAnywayPrompt(saveAnywayKind(category), isEditMode),
      notice: { kind: 'error', error, connection },
      summary: `Could not connect through the SSH tunnel: ${error}`,
    };
  }
  const text = `Connected to ${target || 'the database'} through the SSH tunnel`;
  return { tone: 'success', legs, notice: { kind: 'success', text }, summary: text };
}

/** What a storage run that verified nothing adds to its summary line (Save runs the test again and keeps nothing). */
/** The services are in the form above the strip (final UX review UX-09). */
export const STORAGE_NOTHING_VERIFIED = 'Nothing was verified. Fix a service in the form and test again.';

function storageModel(outcome: ConnectionTestOutcome, legs: TestLeg[], isEditMode: boolean): ResultStripModel {
  const summary = outcome.legsSummary ?? '';
  if (outcome.result && !outcome.result.success) {
    // Save anyway keeps the connection untested (rule 3b.7: it is offered on every failure).
    const text = summary ? `${summary}. ${STORAGE_NOTHING_VERIFIED}` : STORAGE_NOTHING_VERIFIED;
    const withheld = storageSaveAnywayRefusal(outcome.storageFailures);
    return {
      tone: 'failure', legs, legsSummary: { text, failed: true }, summary: summary || 'Nothing was verified',
      // C2 review C2-05: the credential, the resource ID, the endpoint override or the account the failure is about.
      fieldSelector: storageFieldSelector(outcome.storageFailures, outcome.connection),
      ...(withheld ? { saveAnywayWithheld: withheld } : { saveAnyway: saveAnywayPrompt('storage', isEditMode) }),
    };
  }
  const partial = legs.some((leg) => leg.status !== 'ok');
  return { tone: partial ? 'warning' : 'success', legs, legsSummary: summary ? { text: summary, failed: false } : undefined, summary: summary || 'Connected' };
}

/**
 * Every text the model presents, through the one safe formatter (C2 review r2, C2-03): the notice, the success and
 * warning lines, the legs' labels, chips and details, the legs' summary line, the short tier's line and the Save anyway
 * wording. A driver's error or a service's sentence can echo a connect string or a descriptor — whatever renders the
 * model (the strip, the short tier, Details) shows only this.
 */
function safeModel(model: ResultStripModel): ResultStripModel {
  const safe = redactTargetSecrets;
  const { notice } = model;
  return {
    ...model,
    summary: safe(model.summary),
    ...(notice ? { notice: notice.kind === 'error' ? { ...notice, error: safe(notice.error), ...(notice.title ? { title: safe(notice.title) } : {}) } : { ...notice, text: safe(notice.text) } } : {}),
    ...(model.legsSummary ? { legsSummary: { ...model.legsSummary, text: safe(model.legsSummary.text) } } : {}),
    ...(model.legs ? { legs: model.legs.map((leg) => ({ ...leg, label: safe(leg.label), detail: safe(leg.detail), ...(leg.details ? { details: safe(leg.details) } : {}) })) } : {}),
    ...(model.saveAnyway ? { saveAnyway: safe(model.saveAnyway) } : {}),
    ...(model.saveAnywayWithheld ? { saveAnywayWithheld: safe(model.saveAnywayWithheld) } : {}),
  };
}

/**
 * The strip for `outcome` (the answer still describing the form), or the progress line while a test runs. Null when
 * there is nothing to show — the strip then takes no height. All its text is safe to show (`safeModel`).
 */
export function resultStripModel(outcome: ConnectionTestOutcome | null, options: { testing: boolean; isEditMode: boolean }): ResultStripModel | null {
  const model = buildModel(outcome, options);
  return model && safeModel(model);
}

function buildModel(outcome: ConnectionTestOutcome | null, options: { testing: boolean; isEditMode: boolean }): ResultStripModel | null {
  if (!outcome) return options.testing ? { tone: 'progress', summary: 'Testing the connection…' } : null;
  if (outcome.stepwise) return stepwiseModel(outcome, outcome.stepwise, options.isEditMode);
  if (outcome.legs) return storageModel(outcome, outcome.legs, options.isEditMode);
  const result = outcome.result;
  if (!result) return null;
  if (result.success) return { tone: 'success', notice: { kind: 'success', text: result.message }, summary: result.message };
  if (result.variant === 'warning') return { tone: 'warning', notice: { kind: 'warning', text: result.message }, summary: result.message };
  // An Azure Storage test that failed before any service answered (the IPC refused the request): its structured error
  // decides, as for a report (C2-02).
  if (outcome.storageFailures) {
    const storageWithheld = storageSaveAnywayRefusal(outcome.storageFailures);
    return {
      tone: 'failure', notice: { kind: 'error', error: result.message, connection: result.connection }, summary: result.message,
      fieldSelector: storageFieldSelector(outcome.storageFailures, outcome.connection),
      ...(storageWithheld ? { saveAnywayWithheld: storageWithheld } : { saveAnyway: saveAnywayPrompt('storage', options.isEditMode) }),
    };
  }
  const { category } = classifyDbError(result.connection?.engine, result.message, result.errorCode);
  return {
    tone: 'failure',
    notice: { kind: 'error', error: result.message, connection: result.connection, errorCode: result.errorCode },
    saveAnyway: saveAnywayPrompt(saveAnywayKind(category), options.isEditMode),
    fieldSelector: errorFieldSelector(category, result.connection),
    summary: result.message,
  };
}

/**
 * Why Save anyway is refused for the answer that describes the form, or undefined when it is allowed (no answer, a
 * success, an ordinary failure). The strip and the save handler read the same rule (C2 review C2-01 / C2-02).
 */
export function saveAnywayRefusal(outcome: ConnectionTestOutcome | null): string | undefined {
  return resultStripModel(outcome, { testing: false, isEditMode: false })?.saveAnywayWithheld;
}
